import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { useCreateInterface, useSaveCredentials } from "@/shared/api/interfaces";
import { FieldLabel, GhostButton, Note, PrimaryButton, TextInput } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { Steps } from "./Steps";

import type { Interface } from "@/shared/types";
import type { SetupProps } from "../types";

/** Slack's answers, in this dialog's words. */
const WORDS = {
  3110001: "Slack rejected that token — copy the Bot User OAuth Token again.",
  3110002: "That Slack app is already connected to an agent.",
  3110003: "Couldn't reach Slack — try again.",
};

const titleFor = (existing?: Interface) =>
  !existing ? "Connect Slack" : existing.connection === "broken" ? "Reinstall the Slack app" : "Finish connecting Slack";

/**
 * Slack takes two steps, because the app needs our address and we need its
 * token. Step one creates the interface and opens Slack on our manifest; step
 * two sends the two values Slack shows once the app is installed. Closing in
 * between leaves a "Needs setup" row that reopens here, ready for step two.
 */
export function SlackSetup({ agent, existing, onClose }: SetupProps) {
  const create = useCreateInterface();
  const save = useSaveCredentials();
  const [started, setStarted] = useState(existing);
  const [filled, setFilled] = useState({ bot_token: false, signing_secret: false });
  const meta = catalogFor("slack")!;

  const openSlack = async () => {
    // Opened inside the click: a window opened after an await is what pop-up blockers block.
    const tab = window.open("", "_blank");
    try {
      const row = started ?? (await create.send({ agent_id: agent.id, type: "slack" }));
      setStarted(row);
      if (!tab || !row.setup.manifest_url) return tab?.close();
      tab.opener = null;
      tab.location.href = row.setup.manifest_url;
    } catch {
      tab?.close(); // FormError shows why, under the button.
    }
  };

  const connect = async (values: Record<string, string>) => {
    if (!started) return;
    const value = (name: string) => (values[name] ?? "").trim();
    try {
      await save.send({ id: started.id, credentials: { bot_token: value("bot_token"), signing_secret: value("signing_secret") } });
      toast.success("Slack is connected", { description: `Invite the bot to a channel and @mention it to reach ${agent.name}.` });
      onClose();
    } catch {
      // FormError shows it under the fields.
    }
  };

  // Until step one has run there is no interface to send them to, and the fields say so.
  const field = (name: "bot_token" | "signing_secret", label: string, where: string, placeholder: string) => (
    <div className={started ? undefined : "opacity-50"}>
      <FieldLabel>
        {label} <span className="font-normal text-[var(--text-tertiary)]">· {where}</span>
      </FieldLabel>
      <TextInput
        name={name}
        aria-label={label}
        type="password"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        disabled={!started}
        onChange={(v) => {
          setFilled((f) => ({ ...f, [name]: !!v.trim() }));
          if (save.error) save.reset();
        }}
      />
    </div>
  );

  return (
    <InterfaceDialog
      meta={meta}
      title={titleFor(existing)}
      description={existing?.error ?? meta.desc}
      onSubmit={connect}
      onClose={onClose}
      footer={
        <>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="submit" disabled={!started || !filled.bot_token || !filled.signing_secret || save.isPending}>
            {save.isPending ? "Connecting…" : "Connect"}
          </PrimaryButton>
        </>
      }
    >
      <Steps
        items={[
          <>
            Create the app from our template — Slack opens with everything filled in.
            <div className="mt-2 space-y-2">
              <GhostButton type="button" onClick={openSlack} disabled={create.isPending}>
                Create the Slack app <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
              </GhostButton>
              <FormError error={create.error} />
            </div>
          </>,
          <>Pick your workspace and click Create, then Install to Workspace and Allow.</>,
          <>Paste the two values Slack shows you:</>,
        ]}
      />
      {field("bot_token", "Bot User OAuth Token", "OAuth & Permissions", "xoxb-…")}
      {field("signing_secret", "Signing Secret", "Basic Information", "32 characters")}
      <FormError error={save.error} words={WORDS} />
      {!existing && <LinkedAppsWarning agent={agent} />}
      <Note>Then invite the bot to a channel with /invite @its-name, and @mention it there.</Note>
    </InterfaceDialog>
  );
}
